import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { pb } from '@/core/api';
import type { Creator } from '@/types';
import { useCreatorFilters } from './use-creator-filters';
import { useAuth } from '@/hooks/use-auth';
import { buildFiltersBundle } from './use-creators-filters';
import { CREATOR_LIST_FIELDS } from './creator-list-fields';

const PER_PAGE = 20;

type ListData = { items: Creator[]; totalPages: number; totalItems: number };

type MainArgs = { page: number; filter: string; sort: string; expand: string };

// Los alertados tienen pestaña propia ("Alertados"), así que buildSort ya
// no antepone -alert_pending_at al sort del usuario: cada vista es una
// partición sin solape y el count/paginación que devuelve PB cuadra con lo
// que muestra la pestaña. Sin side-fetch ni merge especial en página 1 —
// TanStack Query devuelve la página tal cual PB la sirve.
//
// Nota histórica: el admin tuvo brevemente un endpoint custom
// (/api/admin/leads) para bypassear el coste de evaluación de la listRule
// fila a fila. Los índices que entraron con #855/#857 ya mantienen la
// query por debajo de 500ms para el admin, así que volvemos al endpoint
// estándar — más simple, count correcto por construcción y sin el
// converter PB→SQL que generó tres regresiones consecutivas (#861, #863,
// #865). El hook backend queda dormido por compatibilidad y se borra en
// una limpieza separada.
function useMainList(args: MainArgs) {
  return useQuery({
    queryKey: [
      'creators',
      { page: args.page, filter: args.filter, sort: args.sort },
    ],
    queryFn: async () =>
      pb.collection('leads').getList<Creator>(args.page, PER_PAGE, {
        filter: args.filter,
        sort: args.sort,
        expand: args.expand,
        fields: CREATOR_LIST_FIELDS,
      }),
    placeholderData: keepPreviousData,
  });
}

export function useCreators(): {
  data: ListData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isPlaceholderData: boolean;
} {
  const filters = useCreatorFilters();
  const { user } = useAuth();
  const { filter, sort, expand } = buildFiltersBundle({
    filters,
    role: user?.role,
    userId: user?.id,
  });
  const main = useMainList({ page: filters.page, filter, sort, expand });
  return {
    data: main.data,
    isLoading: main.isLoading,
    isFetching: main.isFetching,
    isPlaceholderData: main.isPlaceholderData,
  };
}
